import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { IMAGES, CONTACT } from "../../data/tilio";
import { Reveal } from "./Shared";

export default function Holistic() {
    const [open, setOpen] = useState(false);

    return (
        <section data-testid="holistic-section" className="border-t border-stone/70 bg-[#F5F8F4]">
            <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-6 py-24 lg:grid-cols-2 lg:gap-20 lg:px-10 lg:py-32">
                <Reveal className="relative">
                    <img
                        src={IMAGES.holistic}
                        alt="Małgorzata Dec – masażystka, masaż i seanse gongów w Tilio"
                        title="Małgorzata Dec, masażystka prowadząca gabinet masażu i medytacji około 100 metrów od domków Tilio. W gabinecie dostępne są masaże, seanse gongów i praktyki wspierające ciało."
                        data-testid="holistic-image"
                        loading="lazy"
                        className="relative w-full h-auto"
                    />
                </Reveal>
                <div>
                    <Reveal>
                        <p className="overline-label" data-testid="holistic-eyebrow">04 — Tilio × Małgorzata Dec</p>
                        <h2 className="mt-5 font-serif text-[clamp(2.1rem,1.2rem+2.4vw,3.9rem)] leading-[1.08] tracking-[-1.2px] text-pine" data-testid="holistic-title">
                            Gabinet masażu i medytacji<br />100 metrów od domków.
                        </h2>
                        <p className="mt-5 max-w-sm text-[17px] leading-[1.6] text-sage md:text-lg">
                            Masaże, seanse gongów i praktyki wspierające ciało — w kameralnym gabinecie
                            tuż obok Tilio. Zabiegi umawiamy indywidualnie.
                        </p>
                        <button
                            data-testid="holistic-more-btn"
                            onClick={() => setOpen(true)}
                            className="btn-primary mt-9"
                        >
                            Dowiedz się więcej
                        </button>
                    </Reveal>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[70] flex items-end justify-center bg-pine/50 backdrop-blur-sm sm:items-center sm:p-6"
                        onClick={() => setOpen(false)}
                        data-testid="holistic-dialog"
                    >
                        <motion.div
                            initial={{ y: 60, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 60, opacity: 0 }}
                            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            data-lenis-prevent
                            className="max-h-[92vh] w-full max-w-lg overflow-y-auto border border-stone bg-ivory p-8 md:p-10"
                        >
                            <div className="flex items-start justify-between gap-6">
                                <h3 className="font-serif text-3xl tracking-[-1.2px] text-pine">Gabinet Małgorzaty Dec</h3>
                                <button
                                    data-testid="holistic-dialog-close"
                                    onClick={() => setOpen(false)}
                                    className="flex h-9 w-9 shrink-0 items-center justify-center text-pine"
                                    aria-label="Zamknij"
                                >
                                    <X className="h-4 w-4" strokeWidth={1.25} />
                                </button>
                            </div>
                            <ul className="mt-6 border-t border-stone">
                                {[
                                    "Masaż klasyczny i relaksacyjny",
                                    "Seanse gongów i mis",
                                    "Praktyki oddechowe",
                                    "Konsultacje holistyczne",
                                ].map((item) => (
                                    <li key={item} className="border-b border-stone/70 py-3.5 text-base text-sage">{item}</li>
                                ))}
                            </ul>
                            <p className="mt-8 text-[13px] font-semibold uppercase tracking-[3px] text-sage">
                                Małgorzata Dec — człowiek przed metodą
                            </p>
                            <p className="mt-4 text-base leading-[1.6] text-sage">
                                Od ponad 30 lat pracuje z ciałem i wspiera ludzi w odzyskiwaniu kontaktu ze sobą.
                                Zamiast jednej „słusznej” metody wybiera uważność, rozmowę i pracę dopasowaną
                                do konkretnej osoby. Łączy różne formy pracy: masaż, świadomy oddech, medytację,
                                relaksację, dźwięk oraz rozmowę.
                            </p>
                            <blockquote className="mt-5 border-l-2 border-pine/30 pl-5 font-serif text-lg italic leading-relaxed text-pine">
                                „To doświadczenie sprawiło, że dziś patrzę na człowieka szerzej. Interesuje mnie
                                nie tylko to, gdzie pojawia się napięcie, ale również jak żyjesz, jak odpoczywasz,
                                co Cię obciąża i czego w danym momencie naprawdę potrzebujesz.”
                            </blockquote>
                            <p className="mt-6 text-base leading-relaxed text-sage">
                                Terminy umawiamy indywidualnie — napisz na{" "}
                                <a href={`mailto:${CONTACT.email}`} className="link-underline text-pine">{CONTACT.email}</a>{" "}
                                lub zaznacz zainteresowanie zabiegiem w zapytaniu o pobyt.
                            </p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
